import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { dataUrlToFile } from "utils/common";

import { DEFAULT_SIGNATURE_SUBMIT_TEXT } from "./signature-desk.component";
import "./type-signature-fonts.css";
import {
  MAX_SIGNATURE_FONT_SIZE,
  MIN_SIGNATURE_FONT_SIZE,
  SIGNATURE_FONT_STYLES,
  type SignatureFontStyle,
  fitFontSize,
  signatureFontShorthand,
  trimCanvasAlpha
} from "./type-signature.util";

/**
 * 700 is load-bearing: the picker validates every captured file against its
 * `maxWidth` (default 700) AFTER hand-signature-only surfaces have already
 * submitted, so a wider export would upload and then show an error banner.
 */
const EXPORT_WIDTH = 700;
const EXPORT_HEIGHT = 220;
const EXPORT_SIDE_MARGIN = 30;
const EXPORT_VERTICAL_MARGIN = 10;

const MAX_NAME_LENGTH = 60;

const PLACEHOLDER_NAME = "Your Name";

type TypeSignatureDeskProps = {
  width: number;
  height: number;
  beforeButtonSlot?: ReactNode;
  isSubmitButton?: boolean;
  submitButtonText?: string;
  isLoading?: boolean;
  onCapture: (file: File) => void;
};

/**
 * Typed-signature desk (SN-19542): the signer types their name, picks a
 * script style, and confirm renders it to a transparent PNG through the same
 * `onCapture` contract as the draw and Topaz desks.
 */
export default function TypeSignatureDesk({
  width,
  isSubmitButton,
  isLoading,
  submitButtonText = DEFAULT_SIGNATURE_SUBMIT_TEXT,
  beforeButtonSlot,
  onCapture
}: TypeSignatureDeskProps) {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [selectedStyle, setSelectedStyle] = useState<SignatureFontStyle>(
    SIGNATURE_FONT_STYLES[0]
  );
  const [isExporting, setIsExporting] = useState(false);

  const trimmedName = name.trim();
  const cardText = trimmedName || PLACEHOLDER_NAME;
  const styleButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // WAI-ARIA radiogroup contract: arrow keys move the selection and focus
  // follows it, so the announced "radio" semantics match real behavior.
  const handleStyleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
      const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
      if (!forward && !backward) return;
      event.preventDefault();
      const count = SIGNATURE_FONT_STYLES.length;
      const currentIndex = SIGNATURE_FONT_STYLES.findIndex(
        (style) => style.label === selectedStyle.label
      );
      const nextIndex = (currentIndex + (forward ? 1 : -1) + count) % count;
      setSelectedStyle(SIGNATURE_FONT_STYLES[nextIndex]);
      styleButtonRefs.current[nextIndex]?.focus();
    },
    [selectedStyle.label]
  );

  // Start fetching all four faces as soon as the desk mounts, so the cards
  // and preview render in their real fonts by the time the signer looks at
  // them. Optional-chained: jsdom has no FontFaceSet.
  useEffect(() => {
    SIGNATURE_FONT_STYLES.forEach((style) => {
      document.fonts?.load(signatureFontShorthand(style, 32), PLACEHOLDER_NAME);
    });
  }, []);

  // The DOM preview approximates the export's fit so what the signer sees is
  // what the PNG contains; the export recomputes precisely against ctx.
  const previewFontSize = useMemo(() => {
    if (!trimmedName) return 36;
    return Math.max(
      18,
      Math.min(36, Math.round((width * 1.6) / trimmedName.length))
    );
  }, [trimmedName, width]);

  const handleCapture = useCallback(async () => {
    if (!trimmedName || isExporting) return;
    setIsExporting(true);
    try {
      // The face must be resolved before drawing — a still-loading font makes
      // fillText silently bake the fallback into the PNG (AC10). The same
      // shorthand string is used for load and ctx.font so they cannot drift.
      await document.fonts?.load(
        signatureFontShorthand(selectedStyle, MAX_SIGNATURE_FONT_SIZE),
        trimmedName
      );

      const canvas = document.createElement("canvas");
      canvas.width = EXPORT_WIDTH;
      canvas.height = EXPORT_HEIGHT;
      const context = canvas.getContext("2d");
      if (!context) return;

      let size = fitFontSize(
        (text, font) => {
          // Measure with the exact shorthand later drawn with — weight
          // included — so the fitted size is true to the rendered face.
          const sizePx = Number.parseInt(font, 10);
          context.font = signatureFontShorthand(selectedStyle, sizePx);
          return context.measureText(text).width;
        },
        trimmedName,
        selectedStyle.family,
        EXPORT_WIDTH - EXPORT_SIDE_MARGIN * 2
      );
      context.font = signatureFontShorthand(selectedStyle, size);
      // Script faces paint far outside their em box (Homemade Apple's ink is
      // ~1.9x the font size), so fit and center by ACTUAL ink extents — a
      // middle-baseline draw at max size bakes clipped descenders into the
      // PNG. Ink metrics are measured from the alphabetic baseline.
      let metrics = context.measureText(trimmedName);
      let inkHeight =
        metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      const maxInkHeight = EXPORT_HEIGHT - 2 * EXPORT_VERTICAL_MARGIN;
      if (inkHeight > maxInkHeight) {
        size = Math.max(
          MIN_SIGNATURE_FONT_SIZE,
          Math.floor((size * maxInkHeight) / inkHeight)
        );
        context.font = signatureFontShorthand(selectedStyle, size);
        metrics = context.measureText(trimmedName);
        inkHeight =
          metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      }
      context.fillStyle = theme.palette.common.black;
      context.textAlign = "center";
      context.textBaseline = "alphabetic";
      const baselineY =
        (EXPORT_HEIGHT +
          metrics.actualBoundingBoxAscent -
          metrics.actualBoundingBoxDescent) /
        2;
      context.fillText(trimmedName, EXPORT_WIDTH / 2, baselineY);

      const base64 = trimCanvasAlpha(canvas).toDataURL("image/png");
      const file = await dataUrlToFile(base64, "signature.png");
      onCapture(file);
    } finally {
      setIsExporting(false);
    }
  }, [
    trimmedName,
    isExporting,
    selectedStyle,
    theme.palette.common.black,
    onCapture
  ]);

  return (
    <Box sx={{ width }}>
      <TextField
        fullWidth
        label="Full name"
        placeholder="Type your full name"
        value={name}
        inputProps={{
          maxLength: MAX_NAME_LENGTH,
          "data-testid": "type-signature-name",
          // A field labelled "Full name" reads to a password manager as an
          // identity field: 1Password drops its suggestion list right over the
          // style cards below and offers to save the signer's name as a login.
          // autoComplete alone does not stop them — each vendor honours only
          // its own opt-out attribute.
          autoComplete: "off",
          spellCheck: false,
          "data-1p-ignore": true,
          "data-lpignore": "true",
          "data-form-type": "other"
        }}
        onChange={(event) => setName(event.target.value)}
      />
      <Box
        role="radiogroup"
        aria-label="Signature style"
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          mt: 1.5
        }}
      >
        {SIGNATURE_FONT_STYLES.map((style, styleIndex) => {
          const selected = style.label === selectedStyle.label;
          return (
            <Box
              key={style.label}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${style.label} signature style`}
              ref={(element: HTMLButtonElement | null) => {
                styleButtonRefs.current[styleIndex] = element;
              }}
              tabIndex={selected ? 0 : -1}
              onKeyDown={handleStyleKeyDown}
              onClick={() => setSelectedStyle(style)}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                border: "1.5px solid",
                borderColor: selected
                  ? theme.palette.primary.main
                  : theme.palette.grey[300],
                backgroundColor: selected
                  ? theme.palette.primary.light
                  : theme.palette.common.white,
                px: 1.5,
                py: 1.25,
                textAlign: "center",
                overflow: "hidden"
              }}
            >
              <Typography
                component="span"
                noWrap
                sx={{
                  fontFamily: style.family,
                  fontWeight: style.weight,
                  fontSize: 22,
                  // noWrap clips the span to its line box, and script ink runs
                  // ~1.9x the font size (Homemade Apple) — 2 keeps swashes
                  // inside while noWrap keeps the horizontal ellipsis.
                  lineHeight: 2,
                  color: theme.palette.text.primary,
                  display: "block"
                }}
              >
                {cardText}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                {style.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box
        data-testid="type-signature-preview"
        sx={{
          mt: 1.5,
          height: 104,
          borderRadius: 1,
          border: "1px dashed",
          borderColor: theme.palette.grey[400],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden"
        }}
      >
        <Typography
          component="span"
          sx={
            trimmedName
              ? {
                  fontFamily: selectedStyle.family,
                  fontWeight: selectedStyle.weight,
                  fontSize: previewFontSize,
                  // Deliberately NOT noWrap: its overflow-hidden would clip
                  // swashes to the line box. The parent preview Box bounds
                  // the ink instead.
                  whiteSpace: "nowrap",
                  lineHeight: "normal",
                  color: theme.palette.text.primary
                }
              : {
                  color: theme.palette.text.secondary,
                  typography: "body2"
                }
          }
        >
          {trimmedName || "Signature preview"}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        component="p"
        sx={{ color: theme.palette.text.secondary, mt: 1.5, mb: 0 }}
      >
        By selecting {submitButtonText}, I agree that the typed signature above
        is the electronic representation of my signature for all purposes, the
        same as a pen-and-paper signature.
      </Typography>
      <Stack rowGap={2} sx={{ mt: 2 }}>
        {beforeButtonSlot}
        <LoadingButton
          type={isSubmitButton ? "submit" : undefined}
          variant="contained"
          loading={isLoading || isExporting}
          disabled={!trimmedName}
          onClick={handleCapture}
        >
          {submitButtonText}
        </LoadingButton>
      </Stack>
    </Box>
  );
}
